import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { ArrowRight, BadgeCheck, BriefcaseBusiness, ClipboardCheck, FileUser, Globe2, ListChecks, SearchCheck, ShieldCheck, Sparkles, UsersRound } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const copy = {
  zh: {
    brand: "启序慧聘",
    lang: "EN",
    nav: ["能力", "筛选", "漏斗"],
    label: "独立案例站点 08 / 招聘 HR + 简历智能筛选",
    title: "一个能解释筛选理由、并保留公平边界的 AI 招聘网站。",
    subtitle: "面向 HR、招聘负责人和业务面试官，基于岗位要求与简历证据做匹配评分、风险提醒和面试问题生成。",
    primary: "开始筛选",
    secondary: "查看能力",
    featuresTitle: "AI 招聘能力",
    featuresSubtitle: "把简历筛选从主观浏览变成可复核的证据链。",
    features: [
      ["岗位匹配", "按技能、经验、项目复杂度和行业背景做匹配分析。"],
      ["风险识别", "识别经历缺口、成果不清和职责边界不明。"],
      ["面试问题", "根据简历疑点生成结构化追问。"],
      ["公平边界", "不使用年龄、性别、婚育等敏感信息做筛选。"]
    ],
    deskTitle: "AI 简历筛选工作台",
    deskSubtitle: "后端直连 MiniMax，输出评分、依据、缺口、面试问题和下一轮建议。",
    job: "职位要求",
    resume: "候选人简历",
    run: "智能筛选",
    sample: "填入示例",
    waiting: "等待职位和简历输入。",
    funnelTitle: "招聘漏斗与合规",
    funnelSubtitle: "真实 HR 系统需要效率，也需要可解释、公平和可审计。",
    panels: [
      ["证据筛选", "每个判断都应对应简历中的项目、技能或成果。"],
      ["面试协同", "把 AI 问题同步给业务面试官，减少重复沟通。"],
      ["公平招聘", "敏感属性不进入模型判断，并保留人工复核。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 8 个真实独立网站"
  },
  en: {
    brand: "Qixu Hire",
    lang: "中文",
    nav: ["Features", "Screen", "Funnel"],
    label: "Standalone Case Site 08 / HR + AI Resume Screening",
    title: "An AI hiring site that explains screening decisions and keeps fair boundaries.",
    subtitle: "For HR and interview teams: score candidates from job requirements and resume evidence, with risks and interview questions.",
    primary: "Start screening",
    secondary: "View features",
    featuresTitle: "AI Hiring Capabilities",
    featuresSubtitle: "Turn resume review into a reviewable evidence chain.",
    features: [
      ["Job match", "Analyze skills, experience, project complexity and industry background."],
      ["Risk detection", "Find gaps, unclear achievements and fuzzy ownership."],
      ["Interview questions", "Generate structured follow-up questions from resume signals."],
      ["Fair boundaries", "Do not use age, gender, marital or family status for screening."]
    ],
    deskTitle: "AI Resume Screening Desk",
    deskSubtitle: "The backend calls MiniMax directly for score, evidence, gaps, questions and next-round advice.",
    job: "Job requirements",
    resume: "Resume",
    run: "Screen candidate",
    sample: "Use sample",
    waiting: "Waiting for job and resume input.",
    funnelTitle: "Hiring Funnel and Compliance",
    funnelSubtitle: "Real HR systems need efficiency, explainability, fairness and auditability.",
    panels: [
      ["Evidence-based", "Every judgment maps to projects, skills or outcomes in the resume."],
      ["Interview sync", "Share AI questions with hiring managers to reduce repeated work."],
      ["Fair hiring", "Sensitive attributes are excluded from model judgment and reviewed by humans."]
    ],
    footer: "Qixu Technology AI case site · eighth real standalone website"
  }
};

const samples = {
  zh: {
    job: "岗位：AI 产品经理。要求：3 年以上 B2B SaaS 产品经验，熟悉大模型应用、RAG、数据分析；能推动设计、研发和销售协作；有从 0 到 1 交付经验。",
    resume: "候选人：负责过企业知识库和客服机器人产品，主导需求调研、原型设计和上线验收；与算法、后端、销售协作完成 3 个客户试点；熟悉 RAG 检索评估、权限控制和数据看板。简历中提到转化率提升，但没有写清楚个人贡献比例。"
  },
  en: {
    job: "Role: AI Product Manager. Requirements: 3+ years in B2B SaaS, familiar with LLM apps, RAG and analytics; able to coordinate design, engineering and sales; shipped 0-to-1 products.",
    resume: "Candidate: worked on enterprise knowledge base and support bot products; led research, prototyping and launch acceptance; collaborated with algorithm, backend and sales teams for 3 pilot customers; familiar with RAG evaluation, permissions and dashboards. Conversion impact is mentioned but personal ownership is unclear."
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [job, setJob] = useState(samples.zh.job);
  const [resume, setResume] = useState(samples.zh.resume);
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setJob(samples[next].job);
    setResume(samples[next].resume);
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function screen() {
    if (!job.trim() || !resume.trim() || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在筛选候选人..." : "Screening candidate...");
    setMeta("");
    try {
      const response = await fetch("/api/screen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job, resume, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法完成筛选。" : "Network error. Screening unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>聘</span><strong>{t.brand}</strong></a>
        <nav><a href="#features">{t.nav[0]}</a><a href="#desk">{t.nav[1]}</a><a href="#funnel">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#desk">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#features">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><UsersRound size={22} />Hiring pipeline</div>
          <div className="pipeline">
            <div><BriefcaseBusiness size={18} /> Job profile</div>
            <div><FileUser size={18} /> Resume evidence</div>
            <div><SearchCheck size={18} /> Match score</div>
            <div><ClipboardCheck size={18} /> Interview plan</div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="section-copy"><h2>{t.featuresTitle}</h2><p>{t.featuresSubtitle}</p></div>
        <div className="feature-grid">
          {[SearchCheck, BadgeCheck, ListChecks, ShieldCheck].map((Icon, index) => (
            <article key={t.features[index][0]}><Icon size={24} /><h3>{t.features[index][0]}</h3><p>{t.features[index][1]}</p></article>
          ))}
        </div>
      </section>

      <section className="desk-section" id="desk">
        <div className="desk">
          <div className="section-title"><FileUser size={22} /><div><h2>{t.deskTitle}</h2><p>{t.deskSubtitle}</p></div></div>
          <label>{t.job}<textarea value={job} onChange={(event) => setJob(event.target.value)} /></label>
          <label>{t.resume}<textarea value={resume} onChange={(event) => setResume(event.target.value)} /></label>
          <div className="composer-actions">
            <button onClick={() => { setJob(samples[locale].job); setResume(samples[locale].resume); }}>{t.sample}</button>
            <button className="primary" onClick={screen} disabled={loading || !job.trim() || !resume.trim()}>{t.run}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card">
          <h3>Screening output</h3>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="funnel" id="funnel">
        <div><h2>{t.funnelTitle}</h2><p>{t.funnelSubtitle}</p></div>
        <div className="stats">
          <div><span>候选人</span><strong>246</strong><em>本月</em></div>
          <div><span>AI 初筛</span><strong>68</strong><em>推荐</em></div>
          <div><span>一面通过</span><strong>41%</strong><em>提升</em></div>
        </div>
        <div className="funnel-grid">
          {t.panels.map(([title, content]) => <article key={title}><ShieldCheck size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
